// Modal wizard de 2 pasos para perfil de comprador
// ✅ NUEVO: Optimizado para móvil con fullscreen y bloqueo de body scroll

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import PaymentMethodQuestion from './questions/PaymentMethodQuestion';
import FinancingStatusQuestion from './questions/FinancingStatusQuestion';
import PropertyToSellQuestion from './questions/PropertyToSellQuestion';
import PropertyFeaturesQuestion from './questions/PropertyFeaturesQuestion';

/**
 * Modal wizard para crear/editar perfil de comprador
 * Paso 1: Preguntas obligatorias (1-3)
 * Paso 2: Características opcionales + comentarios
 * ✅ NUEVO: Fullscreen en móvil/tablet para mejor experiencia
 */
export const BuyerProfileWizard = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  initialData = {},
  isEditing = false
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const contentRef = useRef(null);
  
  // Paso 1: Preguntas obligatorias
  const [paymentMethod, setPaymentMethod] = useState(initialData.paymentMethod || '');
  const [financingStatus, setFinancingStatus] = useState(initialData.financingStatus || '');
  const [propertyToSell, setPropertyToSell] = useState(initialData.propertyToSell || '');
  
  // Paso 2: Características opcionales
  const [minBedrooms, setMinBedrooms] = useState(initialData.minBedrooms || null);
  const [minBathrooms, setMinBathrooms] = useState(initialData.minBathrooms || null);
  const [desiredSqm, setDesiredSqm] = useState(initialData.desiredSqm || null);
  const [outdoorSpace, setOutdoorSpace] = useState(initialData.outdoorSpace || '');
  const [reformLevel, setReformLevel] = useState(initialData.reformLevel || '');
  const [elevator, setElevator] = useState(initialData.elevator || '');
  const [parking, setParking] = useState(initialData.parking || '');
  const [additionalComments, setAdditionalComments] = useState(initialData.additionalComments || '');
  
  // Errores
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  // ✅ NUEVO: Bloquear scroll del body cuando modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Scroll al top cuando cambia el step
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!paymentMethod) newErrors.paymentMethod = t('wizard.required');
    if (!financingStatus) newErrors.financingStatus = t('wizard.required');
    if (!propertyToSell) newErrors.propertyToSell = t('wizard.required');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmit = async () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }

    setSaving(true);
    
    const profileData = {
      paymentMethod,
      financingStatus,
      propertyToSell,
      minBedrooms,
      minBathrooms,
      desiredSqm,
      outdoorSpace: outdoorSpace || null,
      reformLevel: reformLevel || null,
      elevator: elevator || null,
      parking: parking || null,
      additionalComments: additionalComments || null,
    };

    try {
      setSubmitError('');
      await onSubmit(profileData);
      onClose();
    } catch (error) {
      console.error('Error guardando perfil:', error);
      setSubmitError(t('wizard.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Container - Fullscreen móvil/tablet, centrado desktop */}
      <div className="fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4">
        
        {/* Modal Content */}
        <div className="relative bg-slate-800 w-full h-full flex flex-col border-slate-700 lg:rounded-2xl lg:shadow-2xl lg:max-w-2xl lg:max-h-[90vh] lg:border overflow-hidden">
          
          {/* Header - Compacto en móvil */}
          <div className="flex items-center justify-between p-4 lg:p-6 border-b border-slate-700 flex-shrink-0">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="text-lg lg:text-xl font-bold text-white truncate">
                {isEditing ? t('wizard.titleEdit') : t('wizard.titleCreate')}
              </h2>
              <p className="text-xs lg:text-sm text-slate-400 mt-1">
                {isEditing ? (
                  t('wizard.subtitleEdit')
                ) : (
                  <Trans i18nKey="wizard.subtitleCreate" components={{ bold: <span className="font-semibold text-amber-400" /> }} />
                )}
              </p>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-700 transition-colors group flex-shrink-0"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
            </button>
          </div>

          {/* Progress indicator - Compacto en móvil */}
          <div className="px-4 lg:px-6 pt-3 lg:pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className={`flex-1 h-2 rounded-full transition-all ${
                currentStep >= 1 ? 'bg-amber-500' : 'bg-slate-700'
              }`} />
              <div className={`flex-1 h-2 rounded-full transition-all ${
                currentStep >= 2 ? 'bg-amber-500' : 'bg-slate-700'
              }`} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-400">{t('wizard.stepOf', { step: currentStep })}</span>
              <span className="text-xs text-slate-400 truncate ml-2">
                {currentStep === 1 ? t('wizard.step1Title') : t('wizard.step2Title')}
              </span>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div 
            ref={contentRef} 
            className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 lg:py-6 space-y-4 lg:space-y-6"
          >
            
            {currentStep === 1 && (
              <>
                <PaymentMethodQuestion
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                  error={errors.paymentMethod}
                />

                <FinancingStatusQuestion
                  value={financingStatus}
                  onChange={setFinancingStatus}
                  error={errors.financingStatus}
                />

                <PropertyToSellQuestion
                  value={propertyToSell}
                  onChange={setPropertyToSell}
                  error={errors.propertyToSell}
                />
              </>
            )}

            {currentStep === 2 && (
              <>
                <PropertyFeaturesQuestion
                  minBedrooms={minBedrooms}
                  setMinBedrooms={setMinBedrooms}
                  minBathrooms={minBathrooms}
                  setMinBathrooms={setMinBathrooms}
                  desiredSqm={desiredSqm}
                  setDesiredSqm={setDesiredSqm}
                  outdoorSpace={outdoorSpace}
                  setOutdoorSpace={setOutdoorSpace}
                  reformLevel={reformLevel}
                  setReformLevel={setReformLevel}
                  elevator={elevator}
                  setElevator={setElevator}
                  parking={parking}
                  setParking={setParking}
                />

                {/* Comentarios adicionales */}
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-2">
                    {t('wizard.commentsLabel')}
                  </label>
                  <textarea
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                    placeholder={t('wizard.commentsPlaceholder')}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-amber-500 resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('wizard.charCount', { count: additionalComments.length })}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Error message */}
          {submitError && (
            <div className="px-4 lg:px-6 py-3 bg-red-500/10 border-t border-red-500/20 flex-shrink-0">
              <p className="text-sm text-red-400">{submitError}</p>
            </div>
          )}

          {/* Footer - Compacto en móvil */}
          <div className="p-3 lg:p-4 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
            <div className="flex items-center justify-between gap-3 lg:gap-4">
              {currentStep === 1 ? (
                <>
                  <button
                    onClick={onClose}
                    className="px-3 lg:px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm lg:text-base font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50"
                  >
                    {t('common.next')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleBack}
                    disabled={saving}
                    className="flex items-center gap-2 px-3 lg:px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm disabled:opacity-50"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t('common.back')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm lg:text-base font-semibold rounded-lg hover:from-emerald-600 hover:to-green-700 transition-all disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {isEditing ? t('wizard.update') : t('wizard.saveAndContinue')}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BuyerProfileWizard;